import { useState, type ReactNode } from 'react'
import type { OnTrack } from '../index.js'
import type { WorkerTier } from '../../core/index.js'
import { formatScore, segmentedTabClass, TabStrip, Skeleton } from '../../core/index.js'
import { ActivityList } from './ActivityList.js'
import { MonthCalendar, type DayRules } from './MonthCalendar.js'
import { ScoreBreakdownTable } from './ScoreBreakdownTable.js'
import { ScoreStanding } from './ScoreStanding.js'
import { NoScoreRing } from './NoScoreRing.js'
import { buildMonthCalendar } from './monthPanelData.js'
import { formatMonth } from './outcomeGroups.js'
import type { PeriodData } from './periodData.js'

type BreakdownTab = 'breakdown' | 'shifts'

const BREAKDOWN_TABS: readonly { id: BreakdownTab; label: string }[] = [
  { id: 'breakdown', label: 'Breakdown' },
  { id: 'shifts', label: 'Shifts' },
]

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

interface TabsProps<Id extends string> {
  /**
   * `unsaved` marks a tab holding edits not yet saved, so leaving it is a visible choice; `badge` is a count of things
   * waiting behind the tab (open callouts, notes not yet seen), shown as a coral pill and left out at zero.
   */
  tabs: readonly { id: Id; label: string; unsaved?: boolean; badge?: number }[]
  active: Id
  onChange: (tab: Id) => void
  /** Space below the strip; the page's own layout may want none. */
  className?: string
}

/** A nested tab strip in the app's one nested style (`SEGMENTED_TABLIST_CLASS`), the same wherever it sits. */
export function Tabs<Id extends string>({ tabs, active, onChange, className = 'mb-4' }: TabsProps<Id>) {
  return (
    <TabStrip activeKey={active} className={className}>
        {tabs.map((tab) => (
          <button key={tab.id} type="button" role="tab" aria-selected={active === tab.id} onClick={() => onChange(tab.id)} className={segmentedTabClass(active === tab.id)}>
            {tab.label}
            {tab.unsaved && <span aria-label="Unsaved changes" title="Unsaved changes" className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-coral align-middle" />}
            {(tab.badge ?? 0) > 0 && (
              <span className={`ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-px text-[10px] font-bold leading-4 ${active === tab.id ? 'bg-white text-marine' : 'bg-coral text-white'}`}>
                {tab.badge}
              </span>
            )}
          </button>
        ))}
    </TabStrip>
  )
}

/** The same strip the Partner Simulator uses, so tabs look alike across the app. */
export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid mb-1.5">{title}</div>
      {children}
    </div>
  )
}

/** The dial on the left: the score in its tier, or an empty ring when the period has none. */
export interface PeriodDial {
  score: number | null
  tier: WorkerTier
  onTrack: OnTrack | null
}

interface PeriodViewProps {
  period: PeriodData
  /** Above the dial: "Current Score", or the month's name for a past one. */
  dialLabel: string
  dial: PeriodDial
  /** Under the dial: "Resets in 12 days", or "Final · Silver". */
  dialNote: string
  rules: DayRules
  /** How the shifts are getting on, for the Shifts tab; absent means they are ready. */
  shiftsStatus?: 'pending' | 'error'
  /** Something for the third column above the activity - the bid window, for the current period. */
  aside?: ReactNode
}

/**
 * One scoring period, laid out the same whichever it is: the dial, the
 * score breakdown with its Breakdown and Shifts tabs, and beside them
 * whatever the caller adds and the period's activity. The current period
 * and every month in the history render through this, so they look and
 * work alike.
 */
export function PeriodView({ period, dialLabel, dial, dialNote, rules, shiftsStatus, aside }: PeriodViewProps) {
  const [breakdownTab, setBreakdownTab] = useState<BreakdownTab>('breakdown')
  const { score } = period

  return (
    <div className="grid md:grid-cols-[auto_1fr_1fr] gap-6 items-start">
      <div className="text-center">
        <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid mb-1.5">{dialLabel}</div>
        {dial.score === null ? <NoScoreRing reason="No score" size="lg" /> : <ScoreStanding score={dial.score} tier={dial.tier} onTrack={dial.onTrack} />}
        <div className="text-[10px] text-ink-mid mt-1.5">{dialNote}</div>
      </div>

      <Section title="Score Breakdown">
        <div className="bg-white border border-marine/15 p-3">
          <Tabs tabs={BREAKDOWN_TABS} active={breakdownTab} onChange={setBreakdownTab} className="mb-2" />
          {score && (
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2 text-[10.5px] text-ink-mid">
              <span>
                {score.score === null ? (
                  <span className="font-bold text-ink">Not enough activity to score this period</span>
                ) : (
                  <>
                    <span className="font-bold text-ink">{formatScore(score.score)}</span> this period from{' '}
                    {/* The count is a way into the Shifts tab, where those dates are. */}
                    <button type="button" onClick={() => setBreakdownTab('shifts')} className="font-bold text-marine underline decoration-marine/40 underline-offset-2 hover:text-marine-dk">
                      {score.scheduledShifts} scheduled {score.scheduledShifts === 1 ? 'shift' : 'shifts'}
                    </button>
                    {/* Punctuality and completion are counted over these, so the number is named here before the rows use it. */}
                    {score.workedShifts < score.scheduledShifts && <>, {score.workedShifts} worked</>}
                  </>
                )}
              </span>
              {score.provisional && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-gold-lt text-ink border border-gold/60 rounded">Provisional</span>}
              <span>computed {formatDateTime(score.computedAt)}</span>
            </div>
          )}
          {breakdownTab === 'breakdown' ? (
            <ScoreBreakdownTable inputs={[...period.breakdown]} />
          ) : shiftsStatus === 'pending' ? (
            <Skeleton label="Loading shifts" />
          ) : shiftsStatus === 'error' ? (
            <div className="text-[11px] text-coral">Could not load this worker’s shift outcomes.</div>
          ) : period.shifts.length === 0 ? (
            <div className="text-[11px] text-ink-mid">No shifts reported this period.</div>
          ) : (
            // Narrow, like a date picker: the days are the dates behind the count, and a day opens its shifts.
            <div className="max-w-[220px]">
              <MonthCalendar key={period.month} calendar={buildMonthCalendar(period.month, period.shifts, rules)} rules={rules} />
            </div>
          )}
        </div>
      </Section>

      <div className="flex flex-col gap-4">
        {aside}
        {period.notifications.length > 0 && (
          <Section title={`${formatMonth(period.month)} Activity`}>
            <ActivityList notifications={period.notifications} />
          </Section>
        )}
      </div>
    </div>
  )
}
